/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 21-08-2026
 *
 * De telling achter het docentendashboard (golf 3f): van de ruwe actielog-statistiek naar de
 * getallen per model en per serie. Overgenomen uit `_rebuildChart` in de oude
 * `dashboard.directive.js`, maar als pure functie — hij leest alleen zijn argumenten en houdt
 * niets vast, zodat het paneel hem in een computed kan draaien.
 *
 * Twee gedragswijzigingen ten opzichte van ng1:
 * - Besluit A (21-08-2026): de telling per model telt op in plaats van te overschrijven. ng1
 *   deed daar een toewijzing waar de totalen ernaast wél optelden, waardoor elke serie die
 *   meerdere acties of typen groepeert (`sim`, `condities`, `fouten`, ...) per model maar één
 *   van zijn rijen liet zien — en de balk dus de donut tegensprak.
 * - Testronde 21-08-2026: modellen die het backend niet meer kent tellen niet mee. ng1 zette ze
 *   als kolom `?` in de grafiek; het zijn verwijderde modellen (de actielog blijft staan) en
 *   opgeruimde meekijk-schaduwmodellen, dus ze zeggen de docent niets.
 */
import {elements} from 'dynalearn-common/lib/elements';
import type {Dashboardv1Gegevens} from '../../services/dashboardv1.service';
import {ACTIETYPES, ELEMENTTYPES, SERIE_PER_KEY, SERIES, type Serie} from './dashboardv1-series';

/**
 * De uitkomst van de telling: alles wat de grafieken en de knoppen nodig hebben.
 */
export interface Telling {
    /** Alle getelde modellen, gesorteerd op het label waaronder ze getoond worden. */
    models: string[];
    /** De modellen die de grafiek daadwerkelijk toont, in dezelfde volgorde. */
    chartmodels: string[];
    /** De labels bij `chartmodels`. */
    labels: string[];
    /** Per model per serie het aantal. */
    perModel: Record<string, Record<string, number>>;
    /** Per serie het totaal over alle getelde modellen. */
    totalen: Record<string, number>;
    /** De series die daadwerkelijk iets te tonen hebben, in weergavevolgorde. */
    series: Serie[];
}

/**
 * Bepaalt op welke actieserie een gelogde actie telt. Geeft `null` terug voor acties die
 * nergens meetellen.
 */
function actieSerie(action: string): string | null {
    for (const [seriekey, acties] of Object.entries(ACTIETYPES)) {
        if (acties.includes(action)) {
            return seriekey === '_skip' ? null : seriekey;
        }
    }
    return 'rest';
}

/**
 * Bepaalt op welke elementserie een gelogd targettype telt, via de typehiërarchie. Geeft `null`
 * terug voor typen die nergens meetellen.
 */
function elementSerie(targettype: string): string | null {
    for (const [seriekey, typen] of Object.entries(ELEMENTTYPES)) {
        if (typen.some(type => elements.isSubtypeOf(targettype, type))) {
            return seriekey === '_skip' ? null : seriekey;
        }
    }
    return null; //wat nergens onder valt telt niet mee
}

/**
 * Telt de normstand van één model bij: per categorie hoeveel er goed, fout en missend is. Een
 * model met meer elementen dan de norm verwacht heeft fouten; heeft de normcheck nog niet
 * gedraaid, dan is het overschot zelf de fout.
 */
function telNorm(
    telling: Record<string, number>,
    totalen: Record<string, number>,
    normtelling: {model: Record<string, {aantal?: number; fouten?: number}>; norm: Record<string, number> | null}
): void {
    //een model zonder normmodel levert wél een telling maar geen verwachting: dan is alles overschot
    const norm = normtelling.norm ?? {};
    for (const key of Object.keys(normtelling.model)) {
        const verwacht = norm[key] || 0;
        const aantal = normtelling.model[key].aantal || 0;
        let fout = normtelling.model[key].fouten || 0;
        let missend = 0;
        if (aantal > verwacht) {
            if (!fout) {
                fout = aantal - verwacht;
            }
        } else {
            missend = verwacht - aantal;
        }
        const goed = aantal - fout;
        for (const [seriekey, waarde] of [[`${key}_goed`, goed], [`${key}_fout`, fout], [`${key}_missend`, missend]] as const) {
            if (!SERIE_PER_KEY.has(seriekey)) {
                continue; //geen serie voor deze categorie
            }
            telling[seriekey] = waarde;
            totalen[seriekey] = (totalen[seriekey] ?? 0) + waarde;
        }
    }
}

/**
 * Telt de binnengekomen statistiek uit tot de getallen per model en per serie.
 * @param gegevens De laatst gepubliceerde momentopname
 * @param detail Het model waarvan het detail getoond wordt, of `null` voor het overzicht
 * @param alleenLive Of alleen de modellen tellen die op dit moment openstaan (stand "Live")
 */
export function bouwTelling(gegevens: Dashboardv1Gegevens, detail: string | null, alleenLive: boolean): Telling {
    const perModel: Record<string, Record<string, number>> = {};
    const totalen: Record<string, number> = {};
    const live = new Set(gegevens.live);
    //in detailmodus telt het filter niet: daar gaat het om dit ene model
    const filterOpLive = alleenLive && !detail;

    /**
     * Telt één rij bij op de juiste serie van het juiste model.
     */
    const tel = (modelId: string, seriekey: string, count: number) => {
        const telling = perModel[modelId] ?? (perModel[modelId] = {});
        telling[seriekey] = (telling[seriekey] ?? 0) + count;
        totalen[seriekey] = (totalen[seriekey] ?? 0) + count;
    };

    for (const rij of gegevens.acties) {
        if (gegevens.onbekend.has(rij.model_id) || (filterOpLive && !live.has(rij.model_id))) {
            continue;
        }
        if (rij.model_id && rij.action && rij.count) {
            const seriekey = actieSerie(rij.action);
            if (seriekey) {
                tel(rij.model_id, seriekey, rij.count);
            }
        }
    }

    //de typehiërarchie is per targettype altijd hetzelfde; onthouden scheelt zoekwerk
    const elementcache: Record<string, string | null> = {};
    for (const rij of gegevens.types) {
        if (gegevens.onbekend.has(rij.model_id) || (filterOpLive && !live.has(rij.model_id))) {
            continue;
        }
        if (rij.model_id && rij.targettype && rij.count) {
            const seriekey = elementcache[rij.targettype] !== undefined
                ? elementcache[rij.targettype]
                : (elementcache[rij.targettype] = elementSerie(rij.targettype));
            if (seriekey) {
                tel(rij.model_id, seriekey, rij.count);
            }
        }
    }

    //de norm telt niet uit de actielog maar uit de laatst opgeslagen normtelling van het model
    for (const modelId of Object.keys(perModel)) {
        for (const serie of SERIES) {
            if (serie.balk === 'norm') {
                perModel[modelId][serie.seriekey] = 0;
            }
        }
        const normtelling = gegevens.modelinfo[modelId]?.normtelling;
        if (normtelling) {
            telNorm(perModel[modelId], totalen, normtelling);
        }
    }

    //alleen series waar iets in zit krijgen een plek in de grafiek
    const series = SERIES.filter(serie => totalen[serie.seriekey]);

    const label = (modelId: string) => {
        const info = gegevens.modelinfo[modelId];
        return info ? `${info.user}: ${info.titel}` : '?';
    };
    const models = Object.keys(perModel).sort((a, b) => label(a).localeCompare(label(b)));

    const chartmodels: string[] = [];
    const labels: string[] = [];
    for (const modelId of models) {
        if (detail) {
            if (modelId === detail) {
                chartmodels.push(modelId);
                labels.push(label(modelId));
            }
            continue;
        }
        if (filterOpLive && !live.has(modelId)) {
            continue;
        }
        //in het overzicht tonen we alleen modellen waar iets te melden is; een model openen of
        //aanmaken telt daarbij niet mee
        const heeftIetsTeMelden = series.some(serie =>
            !['model', 'rest'].includes(serie.seriekey) && !!perModel[modelId][serie.seriekey]);
        if (!live.has(modelId) && !heeftIetsTeMelden) {
            continue;
        }
        chartmodels.push(modelId);
        labels.push(label(modelId));
    }

    return {models, chartmodels, labels, perModel, totalen, series};
}
